import {debounce} from 'lodash';
import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {Heading, IconButton, SearchInput, SegmentedControl} from '@albert-io/atomic';
import {useFluxStore} from 'client/framework';
import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import {ListRowsSkeleton} from 'client/TeacherDashboard/components/ListRowsSkeleton';
import {SortDropdown} from 'client/TeacherDashboard/components/SortDropdown';
import type {SortValue} from 'client/TeacherDashboard/types';

import {getSkillReportPath, SORT_OPTIONS, STATUS_SEGMENTS} from './constants';
import {SkillPathRow} from './SkillPathRow';
import {
  SkillPathsFilteredEmptyState,
  SkillPathsOnboardingEmptyState,
  SkillPathStatusEmptyState
} from './SkillPathsEmptyState';
import {SkillPathsFiltersMenu} from './SkillPathsFiltersMenu';
import type {
  DueWithinFilter,
  SkillPathRowData,
  SkillPathSortValue,
  SkillPathStatusSegment
} from './types';
import {useSkillPathsList} from './useSkillPathsList';

const DEFAULT_SORT: SortValue<SkillPathSortValue> = {field: 'dueDate', order: 'asc'};

const getInitialSegment = (): SkillPathStatusSegment => {
  const hash = window.location.hash.substring(1);
  const match = STATUS_SEGMENTS.find((s) => s.value === hash);
  return match ? match.value : 'open';
};

interface Props {
  classroomId?: string;
}

export const SkillPathsList = ({classroomId}: Props) => {
  // Switching class scope resets filters + sort but keeps the current tab.
  const [segment, setSegment] = useState<SkillPathStatusSegment>(getInitialSegment);

  const handleSegmentChange = (value: string | number) => {
    if (value === segment) {
      return;
    }
    window.location.hash = String(value);
    setSegment(value as SkillPathStatusSegment);
  };

  return (
    <div className='gap-x3 pt-x4 flex flex-col'>
      <Heading as='h2' className='m-x0' size='s'>
        Adaptive Skill Paths
      </Heading>
      <SkillPathsListBody
        key={classroomId ?? 'all'}
        classroomId={classroomId}
        segment={segment}
        onSegmentChange={handleSegmentChange}
      />
    </div>
  );
};

interface BodyProps {
  classroomId?: string;
  segment: SkillPathStatusSegment;
  onSegmentChange: (value: string | number) => void;
}

const SkillPathsListBody = ({classroomId, segment, onSegmentChange}: BodyProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>('all');
  const [dueWithin, setDueWithin] = useState<DueWithinFilter>('schoolYear');
  const [dueDateStart, setDueDateStart] = useState('');
  const [dueDateEnd, setDueDateEnd] = useState('');
  const [sort, setSort] = useState<SortValue<SkillPathSortValue>>(DEFAULT_SORT);

  const debouncedSetSearch = useMemo(() => debounce(setDebouncedSearchTerm, 200), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const actorTeacherId = useFluxStore(masqueradeStore, (s) => s.getUserIdByMasqueradeState() ?? '');

  const {rows, counts, subjectOptions, isLoading, isScopeEmpty} = useSkillPathsList({
    classroomId,
    segment,
    search: debouncedSearchTerm,
    subject,
    dueWithin,
    dueDateStart,
    dueDateEnd,
    sort
  });

  const segmentOptions = useMemo(
    () =>
      STATUS_SEGMENTS.map((s) => ({
        ...s,
        label: (
          <>
            {s.label} <span className='ml-x0-half content-tertiary'>({counts[s.value]})</span>
          </>
        )
      })),
    [counts]
  );

  const clearSearch = () => {
    setSearchTerm('');
    debouncedSetSearch.cancel();
    setDebouncedSearchTerm('');
  };

  const handleEdit = useCallback((_row: SkillPathRowData) => {}, []);

  const hasFiltersOn = !!debouncedSearchTerm || subject !== 'all' || dueWithin !== 'schoolYear';

  if (isScopeEmpty) {
    return <SkillPathsOnboardingEmptyState />;
  }

  return (
    <>
      <div className='gap-x2 flex flex-wrap items-center justify-between'>
        <SegmentedControl
          buttonClassName='font-normal'
          onSelect={onSegmentChange}
          options={segmentOptions}
          selectedIdx={STATUS_SEGMENTS.findIndex((s) => s.value === segment)}
        />
        <div className='gap-x1 flex items-center'>
          <SkillPathsFiltersMenu
            dueWithin={dueWithin}
            dueDateStart={dueDateStart}
            dueDateEnd={dueDateEnd}
            subject={subject}
            subjectOptions={subjectOptions}
            onDueWithinChange={setDueWithin}
            onDueDateChange={(bound, value) =>
              bound === 'start' ? setDueDateStart(value) : setDueDateEnd(value)
            }
            onSubjectChange={setSubject}
          />
          <SortDropdown
            options={SORT_OPTIONS}
            triggerIcon={['far', 'arrow-down-short-wide']}
            triggerLabel='Sort'
            value={sort}
            onChange={setSort}
          />
          {isSearchOpen ? (
            <div
              onBlur={(e) => {
                if (!searchTerm && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
                  setIsSearchOpen(false);
                }
              }}
            >
              <SearchInput
                autoFocus
                className='w-[256px]'
                placeholder='Search'
                rectangle
                value={searchTerm}
                onChange={(_e, value) => {
                  setSearchTerm(value);
                  debouncedSetSearch(value);
                }}
                onReset={() => {
                  clearSearch();
                  setIsSearchOpen(false);
                }}
              />
            </div>
          ) : (
            <IconButton
              color='secondary'
              icon='magnifying-glass'
              label='Search skill paths'
              roundness='square'
              variant='outlined'
              onClick={() => setIsSearchOpen(true)}
            />
          )}
        </div>
      </div>
      <div aria-live='polite' className='sr-only'>
        {`Showing ${rows.length} ${rows.length === 1 ? 'skill path' : 'skill paths'}`}
      </div>
      {isLoading ? (
        <ListRowsSkeleton label='Loading skill paths…' />
      ) : rows.length > 0 ? (
        <ul className='m-x0 p-x0 flex list-none flex-col'>
          {rows.map((row) => (
            <li key={row.id}>
              <SkillPathRow
                row={row}
                reportPath={getSkillReportPath(actorTeacherId, row.id, classroomId)}
                onEdit={handleEdit}
              />
            </li>
          ))}
        </ul>
      ) : hasFiltersOn ? (
        <SkillPathsFilteredEmptyState />
      ) : (
        <SkillPathStatusEmptyState segment={segment} />
      )}
    </>
  );
};
